import React, { createContext, useContext, useEffect, useState } from "react";
import config from "../../config";
import API from "../../axios/API";

const BrandContext = createContext();

export const useBrandContext = () => {
  return useContext(BrandContext);
};

/**
 * This component provides a context for managing brand-related data and state throughout the application.
 *
 * @param {Object} children The child components wrapped within the BrandProvider.
 * @returns {ReactNode} A React element that provides a context for brand-related data and state.
 */
export const BrandProvider = ({ children }) => {
  /**
   * State variables and their setters for managing the active step, page name, brand image path,
   * file error status, brand status, and more.
   */
  const [activeStep, setActiveStep] = useState(0);
  const [pageName, setPageName] = useState("Information");
  const initialImagePath = sessionStorage.getItem("brandImagePath") || "";
  const token = localStorage.getItem("token"); // Retrieve token from sessionStorage
  const [isCreatingBrand, setIsCreatingBrand] = useState(true); // Initialize to true for creating new brand
  const [imagePath, setImagePath] = useState(initialImagePath);
  const [fileError, setFileError] = useState(false);
  const [status1, setStatus1] = useState(1);
  const [path, setPath] = useState(null);
  const [segOpen, setSegOpen] = useState(false);
  const [subSegOpen, setSubSegOpen] = useState(false);
  const [brandViewRefresh, setBrandViewRefresh] = useState(false);

  const [brandStatus, setBrandStatus] = useState({
    informationComplete: false,
    contactComplete: false,
    configurationComplete: false,
    blockingComplete: false,
  });

  const [isBrandDuplicate, setIsBrandDuplicate] = useState(false);
  const [isBrandEmailDuplicate, setIsBrandEmailDuplicate] = useState(false);

  /**
   * Function to retrieve the initial site data.
   *
   * @returns {Object} The initial site data.
   */
  function getInitialSiteData() {
    return {
      brandId: 0,
      account: {
        accountId: "",
        accountName: "",
      },
      brandName: "",
      segment: {
        id: "",
        name: "",
        list: [
          {
            id: "",
            name: "",
          },
        ],
      },
      comments: null,
      brandLogoName: "",
      brandImagePath: imagePath, // Set imagePath only when activeStep is 0
      searchTerm: null,
      user:
        activeStep === 1
          ? [
              {
                userId: 0,
                userName: null,
                password: null,
                roleId: null,
                firstName: null,
                status: null,
                accountId: null,
                partnerId: null,
                languageId: null,
                companyFlag: null,
                statusFlag: null,
                trafficPlusFlag: "",
                faqsFlag: "",
                userTypeId: null,
                modifiedTime: null,
                modifiedBy: null,
                createdBy: null,
                contacts: [
                  {
                    contactId: 0,
                    contactName: "",
                    street1: null,
                    street2: null,
                    city: null,
                    zip: null,
                    state: null,
                    country: null,
                    contactNo: null,
                    email: "",
                    linkedIn: null,
                    skype: null,
                  },
                ],
              },
            ]
          : null,
      brandConfig:
        activeStep === 2
          ? {
              conversionId: 0,
              pixelType1: "",
              pixelType2: "",
              ipAddress: null,
              acceptHeader: null,
              userAgent: null,
              conversionPixel: null,
              conversionType: null,
              brandId: "",
            }
          : {
              conversionId: 0,
              pixelType1: "",
              pixelType2: "",
              ipAddress: null,
              acceptHeader: null,
              userAgent: null,
              conversionPixel: null,
              conversionType: null,
              brandId: "",
            },
      blockingDTOS:
        activeStep === 3
          ? [
              {
                blockingId: "",
                brandId: "",
                siteId: "",
                siteName: "",
                placement: "",
                deviceId: "",
                deviceName: "",
                geoId: "",
                geoName: "",
                modifiedTime: null,
                modifiedBy: null,
              },
            ]
          : null,
      activeStep: activeStep || 0,
      status: status1,
      ipAddress: null,
      acceptHeader: null,
      userAgent: null,
      partnerId: null,
      companyFlag: null,
      modifiedTime: null,
      modifiedBy: null,
      file: {
        brandImagePath: null,
      },
    };
  }

  const [brandData, setBrandData] = useState(getInitialSiteData());
  const [contactIdForUnique, setContactIdUnique] = useState(0);
  const [accountId, setAccountId] = useState(0);


  // const [userIndexStatus, setUserIndexStatus] = useState(brandData.status);

  let bId = brandData.brandId || 0;
  useEffect(() => {
    console.log("brandData-->", brandData);
    if (brandData.length > 0) {
      const accountId =
        brandData[0].account.accountId > 0 ? brandData[0].account.accountId : 0;
      setAccountId(accountId);
    } else {
      setAccountId(brandData.account.accountId ? brandData.account.accountId : accountId);
    }
  }, [brandData,accountId]);

  /**
   * Function to check the uniqueness of a brand name.
   *
   * @param {string} brandName The brand name to be checked for uniqueness.
   * @returns {Promise<string|undefined>} A promise resolving to an error message if the brand name is not unique, otherwise undefined.
   */
  useEffect(() => {
    console.log("accountId-->", accountId);
  }, [accountId]);
  const checkBrandNameUniqueness = async (brandName, setFileError) => {
    // console.log("brandName", brandName);
    console.log("accountId", accountId);
    // console.log("brandId", bId);
    try {
      if (!token) {
        throw new Error("Token not found in sessionStorage.");
      }

      // Create a new FormData object
      const formData = new FormData();

      // Append the brandName and brandId as key-value pairs
      formData.append("brandName", brandName);
      formData.append("accountId", accountId);
      formData.append("brandId", bId); // Convert brandId to a string and append it

      // Make an API call with the FormData
      const response = await API.post(
        config.baseUrl + "/brandservice/brands/find-duplicate-brand",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // Set the content type to form-data
          },
        }
      );

      // console.log("----->", response.data);

      if (response.data.errorCode == 1) {
        setIsBrandDuplicate(false);
        setFileError("brandName", "Brand Name Already Exists");

        // return undefined; // Name is unique
      } else {
        setIsBrandDuplicate(true);
        setFileError("brandName", "");
        // return "Brand name must be unique";
      }
    } catch (error) {
      console.error(error);
      return "Error occurred while checking uniqueness";
    }
  };

  /**
   * Function to check the uniqueness of a brand email.
   *
   * @param {string} email The email to be checked for uniqueness.
   * @returns {Promise<string|undefined>} A promise resolving to an error message if the email is not unique, otherwise undefined.
   */
  const checkBrandEmailUniqueness = async (email) => {
    // console.log("email", email);
    // console.log("contactId", contactIdForUnique);
    try {
      // Create a new FormData object
      const formData = new FormData();

      // Append the brandName and brandId as key-value pairs
      formData.append("email", email);
      // formData.append('contactId', contactIdForUnique !== 0 ? 0 : contactIdForUnique);
      formData.append("contactId", contactIdForUnique);

      // Make an API call with the FormData
      const response = await API.post(
        config.baseUrl + "/brandservice/brands/find-duplicate-email",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // Set the content type to form-data
          },
        }
      );

      // console.log("----->", response.data);

      if (response.data.errorCode === 0) {
        setIsBrandEmailDuplicate(false);
        // setEmailError("Email","");
        // return undefined; // Name is unique
      } else {
        setIsBrandEmailDuplicate(true);
        // setEmailError("Email","Email Already Exists!");
        // return "Email Id must be unique";
      }
    } catch (error) {
      console.error(error);
      return "Error occurred while checking uniqueness";
    }
  };

  /**
   * Function to reset brand data to its initial state.
   */
  const resetBrandData = () => {
    setBrandData(getInitialSiteData());
    setFileError("");
    setImagePath("");
    setStatus1(1);
    setBrandStatus((prev) => ({
      ...prev,
      informationComplete: false,
      configurationComplete: false,
      contactComplete: false,
      blockingComplete: false,
    }));
    setPageName("Information");
    setSegOpen(false);
    setSubSegOpen(false);
    bId = 0;
    setAccountId(0);
    sessionStorage.removeItem("brandImagePath"); // Clear imagePath from sessionStorage
    console.log("Reset Context");
  };

  /**
   * Effect to update sessionStorage when imagePath changes.
   */
  useEffect(() => {
    if (activeStep === 0) {
      sessionStorage.setItem("brandImagePath", imagePath);
    }
  }, [activeStep, imagePath]);

  /**
   * Function to perform a deep comparison between two objects.
   *
   * @param {Object} objA The first object for comparison.
   * @param {Object} objB The second object for comparison.
   * @param {boolean} isCreatingBrand Flag indicating if a brand is being created.
   * @returns {Object} Differences between the two objects.
   */
  function deepValueCompare(objA, objB, isCreatingBrand = true) {
    const differences = {};

    const stack = [{ a: objA, b: objB, path: "" }];

    while (stack.length > 0) {
      const { a, b, path } = stack.pop();

      if (typeof a !== "object" || typeof b !== "object") {
        if (a !== b) {
          differences[path] = { oldValue: a, newValue: b };
        }
      } else {
        for (const key in a) {
          if (a.hasOwnProperty(key)) {
            const newPath = path ? `${path}.${key}` : key;
            if (!b.hasOwnProperty(key)) {
              differences[newPath] = { oldValue: a[key], newValue: undefined };
            } else {
              stack.push({ a: a[key], b: b[key], path: newPath });
            }
          }
        }

        for (const key in b) {
          if (b.hasOwnProperty(key) && !a.hasOwnProperty(key)) {
            const newPath = path ? `${path}.${key}` : key;
            differences[newPath] = { oldValue: undefined, newValue: b[key] };
          }
        }
      }
    }

    if (!isCreatingBrand && differences.hasOwnProperty("file")) {
      delete differences["file"];
    }

    return differences;
  }

  // console.log("Brand Image Path ", path);

  return (
    <BrandContext.Provider
      value={{
        brandData,
        setBrandData,
        activeStep,
        setActiveStep,
        pageName,
        setPageName,
        resetBrandData,
        isCreatingBrand,
        setIsCreatingBrand,
        imagePath,
        setImagePath,
        fileError,
        setFileError,
        checkBrandNameUniqueness,
        deepValueCompare,
        status1,
        setStatus1,
        path,
        setPath,
        setAccountId,
        brandStatus,
        setBrandStatus,
        checkBrandEmailUniqueness,
        segOpen,
        setSegOpen,
        subSegOpen,
        setSubSegOpen,
        bId,
        token,
        setContactIdUnique,
        brandViewRefresh,
        setBrandViewRefresh,
        isBrandDuplicate,
        setIsBrandDuplicate,
        isBrandEmailDuplicate,
        setIsBrandEmailDuplicate,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};
